import { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { dataHealthApi } from '../../lib/api.js'
import { useAuthUser } from '../../store/index.js'
import type {
  AssetFamily,
  AssetPresenceEntry,
  AssetPurgeOutcome,
  DataHealthSummary,
} from '../../types/index.js'
import './DataHealthPanel.css'

const FAMILY_LABELS: Record<AssetFamily, string> = {
  PROGRAM_MEDIA:  'Médias de programme',
  AD_SPOT:        'Spots publicitaires',
  INTERLUDE_CLIP: 'Jingles et habillage',
  CG_ASSET:       'Assets graphiques',
  CG_TEMPLATE:    'Templates CG',
  BUG_LOGO:       'Logos antenne',
  THUMBNAIL:      'Vignettes',
}

/** Ce que la purge fait réellement, par famille — annoncé avant confirmation. */
const FAMILY_PURGE_EFFECT: Record<AssetFamily, string> = {
  PROGRAM_MEDIA:  'le fichier est détaché du programme, qui est conservé',
  AD_SPOT:        'le spot est supprimé, ou désactivé s’il a déjà été diffusé',
  INTERLUDE_CLIP: 'le clip est supprimé, ou désactivé s’il est référencé',
  CG_ASSET:       'l’asset est supprimé',
  CG_TEMPLATE:    'le template est désactivé (des overlays y renvoient)',
  BUG_LOGO:       'rien : un logo antenne se remet en place, il ne se purge pas',
  THUMBNAIL:      'la vignette est détachée du programme ou de la série',
}

const ROOT_LABELS: Record<string, string> = {
  MEDIA:        'Dossier média',
  CG_TEMPLATES: 'Templates CasparCG',
  CG_ASSETS:    'Assets graphiques',
  LOGOS:        'Logos antenne',
  THUMBNAILS:   'Vignettes',
}

const OUTCOME_LABELS: Record<AssetPurgeOutcome['action'], string> = {
  DELETED:       'supprimé',
  DEACTIVATED:   'désactivé',
  FIELD_CLEARED: 'détaché',
  SKIPPED:       'ignoré',
}

function formatSince(iso: string | null): string {
  if (!iso) return '—'
  const hours = Math.floor((Date.now() - Date.parse(iso)) / 3_600_000)
  if (hours < 1) return 'moins d’une heure'
  if (hours < 48) return `${hours} h`
  return `${Math.floor(hours / 24)} j`
}

export const DataHealthPanel = memo(function DataHealthPanel({ channelId }: { channelId: string | null }) {
  const user = useAuthUser()
  const [summary, setSummary] = useState<DataHealthSummary | null>(null)
  const [entries, setEntries] = useState<AssetPresenceEntry[]>([])
  const [family, setFamily]   = useState<AssetFamily | ''>('')
  const [selected, setSelected] = useState<Set<string>>(() => new Set())
  const [busy, setBusy]       = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const canPurge = user?.role === 'ADMIN' || Boolean(user?.permissions.includes('TECHNICAL_SETTINGS_MANAGE'))

  const load = useCallback(async () => {
    const [nextSummary, nextEntries] = await Promise.all([
      dataHealthApi.summary(channelId ?? undefined),
      dataHealthApi.entries({
        channelId: channelId ?? undefined,
        family:    family || undefined,
        status:    'MISSING',
      }),
    ])
    setSummary(nextSummary)
    setEntries(nextEntries)
    // Une ligne disparue de la liste ne doit pas rester cochée en arrière-plan.
    setSelected(previous => new Set(nextEntries.filter(e => previous.has(e.id)).map(e => e.id)))
  }, [channelId, family])

  useEffect(() => { void load().catch(error => setMessage(error.message)) }, [load])

  const run = useCallback(async (key: string, action: () => Promise<string>) => {
    setBusy(key); setMessage('')
    try {
      setMessage(await action())
      await load()
    } catch (error) {
      setMessage(error instanceof Error ? error.message : String(error))
    } finally { setBusy(null) }
  }, [load])

  const rescan = useCallback(() => run('scan', async () => {
    const report = await dataHealthApi.scan()
    return `${report.checked} référence(s) vérifiée(s) — ${report.missing} absente(s), ${report.present} présente(s)` +
      (report.awaitingProbe ? `, ${report.awaitingProbe} en attente d’analyse` : '')
  }), [run])

  const purgeable = useMemo(() => entries.filter(entry => entry.purgeable), [entries])
  const selectedPurgeable = useMemo(
    () => purgeable.filter(entry => selected.has(entry.id)),
    [purgeable, selected],
  )

  const purge = useCallback(() => {
    const affected = [...new Set(selectedPurgeable.map(entry => entry.family))]
      .map(item => `• ${FAMILY_LABELS[item]} : ${FAMILY_PURGE_EFFECT[item]}`)
      .join('\n')
    if (!confirm(
      `Purger ${selectedPurgeable.length} donnée(s) dont le fichier est absent ?\n\n${affected}\n\n` +
      'Ce qui est encore référencé par une diffusion passée est désactivé, jamais supprimé.',
    )) return

    void run('purge', async () => {
      const result = await dataHealthApi.purge(selectedPurgeable.map(entry => entry.id))
      setSelected(new Set())
      return result.skipped
        ? `${result.purged} donnée(s) traitée(s), ${result.skipped} ignorée(s) — ` +
          `${result.outcomes.find(outcome => outcome.action === 'SKIPPED')?.reason ?? ''}`
        : `${result.purged} donnée(s) traitée(s)`
    })
  }, [run, selectedPurgeable])

  const toggle = useCallback((id: string, checked: boolean) => {
    setSelected(previous => {
      const next = new Set(previous)
      if (checked) next.add(id)
      else next.delete(id)
      return next
    })
  }, [])

  const allSelected = purgeable.length > 0 && selectedPurgeable.length === purgeable.length

  return (
    <section className="settings-section data-health" aria-labelledby="data-health-title">
      <div className="settings-section__heading">
        <h2 id="data-health-title" className="settings-section__title">Santé des données</h2>
        <p className="settings-section__description">
          Confrontation périodique de ce que la base référence à ce qui existe réellement sur
          disque : médias, spots, jingles, habillage graphique, vignettes et logos antenne.
          {summary?.checkedAt && ` Dernière vérification il y a ${formatSince(summary.checkedAt)}.`}
        </p>
      </div>

      {/* Une racine absente fausse tout : on le dit avant de montrer des chiffres. */}
      {summary && summary.unmountedRoots.length > 0 && (
        <p className="data-health__warning" role="status">
          Stockage introuvable : {summary.unmountedRoots.map(root => ROOT_LABELS[root] ?? root).join(', ')}.
          La présence des fichiers concernés est indéterminée et aucune purge n’est proposée
          tant que le montage n’est pas rétabli.
        </p>
      )}

      {summary && (
        <div className="data-health__totals">
          <Stat label="Suivies"      value={summary.totals.tracked} />
          <Stat label="Présentes"    value={summary.totals.present} tone="success" />
          <Stat label="Absentes"     value={summary.totals.missing} tone="danger" />
          <Stat label="Indéterminées" value={summary.totals.unknown} tone="warning" />
          <Stat label="Purgeables"   value={summary.totals.purgeable} />
        </div>
      )}

      <div className="data-health__toolbar">
        <select
          className="tp-select"
          value={family}
          onChange={event => setFamily(event.target.value as AssetFamily | '')}
          aria-label="Filtrer par famille de données"
        >
          <option value="">Toutes les familles</option>
          {summary?.byFamily.map(item => (
            <option key={item.family} value={item.family}>
              {FAMILY_LABELS[item.family]} ({item.missing} absente{item.missing > 1 ? 's' : ''})
            </option>
          ))}
        </select>

        <button type="button" className="tp-button" onClick={rescan} disabled={busy !== null}>
          {busy === 'scan' ? 'Vérification…' : 'Vérifier maintenant'}
        </button>

        <button
          type="button"
          className="tp-button tp-button--danger"
          onClick={purge}
          disabled={!canPurge || busy !== null || selectedPurgeable.length === 0}
          title={canPurge ? undefined : 'Permission de gestion technique requise'}
        >
          {busy === 'purge' ? 'Purge…' : `Purger la sélection (${selectedPurgeable.length})`}
        </button>
      </div>

      {message && <p className="data-health__message" role="status">{message}</p>}

      {entries.length === 0 ? (
        <p className="data-health__empty">
          Aucun fichier manquant{family ? ` dans « ${FAMILY_LABELS[family]} »` : ''}. Tout ce que la
          base référence existe sur disque.
        </p>
      ) : (
        <table className="data-health__table">
          <thead>
            <tr>
              <th scope="col" className="data-health__check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  disabled={!canPurge || purgeable.length === 0}
                  onChange={event => setSelected(
                    event.target.checked ? new Set(purgeable.map(entry => entry.id)) : new Set(),
                  )}
                  aria-label="Sélectionner toutes les données purgeables"
                />
              </th>
              <th scope="col">Donnée</th>
              <th scope="col">Famille</th>
              <th scope="col">Chemin attendu</th>
              <th scope="col">Absente depuis</th>
            </tr>
          </thead>
          <tbody>
            {entries.map(entry => (
              <tr key={entry.id} className={entry.purgeable ? undefined : 'data-health__row--pending'}>
                <td className="data-health__check">
                  <input
                    type="checkbox"
                    checked={selected.has(entry.id)}
                    disabled={!canPurge || !entry.purgeable}
                    onChange={event => toggle(entry.id, event.target.checked)}
                    aria-label={`Sélectionner ${entry.label}`}
                    title={entry.purgeable
                      ? undefined
                      : `Absence trop récente (${entry.missingStreak} vérification(s)) — la purge attend confirmation`}
                  />
                </td>
                <td>{entry.label}</td>
                <td>{FAMILY_LABELS[entry.family]}</td>
                <td><code className="data-health__path">{entry.expectedPath}</code></td>
                <td>{formatSince(entry.missingSince)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </section>
  )
})

const Stat = memo(function Stat({ label, value, tone }: {
  label: string; value: number; tone?: 'success' | 'danger' | 'warning'
}) {
  return (
    <div className={`data-health__stat${tone ? ` data-health__stat--${tone}` : ''}`}>
      <span className="data-health__stat-value">{value}</span>
      <span className="data-health__stat-label">{label}</span>
    </div>
  )
})
